import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import QureLayout from '../components/qure/QureLayout';
import API from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const QureLibrary = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_experiments: 0,
    upcoming_count: 0,
    missed_count: 0,
    completed_count: 0
  });
  const [experiments, setExperiments] = useState({
    upcoming: [],
    missed: [],
    completed: []
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const modalRef = useRef(null);

  useEffect(() => {
    fetchLibraryData();
  }, []);

  useEffect(() => {
    // Handle modal backdrop click
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) && event.target.classList.contains('modal-backdrop')) {
        setShowModal(false);
      }
    };

    if (showModal) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);

  const fetchLibraryData = async () => {
    try {
      setLoading(true);
      const response = await API.get('/qure/library/experiments');
      if (response.data.ok) {
        setExperiments(response.data.experiments);
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching library data:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Helper function to extract step number from step text
  const extractStepNumber = (stepText) => {
    if (!stepText) return 0;
    // Try to match patterns like "Step 1:", "Step 2:", "1.", "2.", etc.
    const match = stepText.match(/(?:Step\s*)?(\d+)[:.)]/i);
    return match ? parseInt(match[1]) : 0;
  };

  // Helper function to sort steps by step number
  const sortStepsByNumber = (steps) => {
    return steps.sort((a, b) => {
      const numA = extractStepNumber(a);
      const numB = extractStepNumber(b);
      if (numA === 0 && numB === 0) return 0; // Keep original order if no numbers found
      if (numA === 0) return 1; // Put steps without numbers at the end
      if (numB === 0) return -1;
      return numA - numB;
    });
  };

  const viewExperiment = (category, index) => {
    const exp = experiments[category][index];
    if (!exp) return;

    // Parse experiment steps and sort by step number
    let steps = exp.step_of_protocol ? exp.step_of_protocol.split('`').filter(step => step.trim() !== '') : [];
    // Sort steps by step number to ensure correct sequence
    steps = sortStepsByNumber(steps);
    
    setSelectedExperiment({
      ...exp,
      category,
      parsedSteps: steps
    });
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const completionPercentage = stats.total_experiments > 0 
    ? Math.round((stats.completed_count / stats.total_experiments) * 100) 
    : 0;

  if (loading) {
    return (
      <QureLayout>
        <div className="qure-library-container">
          <div className="text-center py-5">
            <div className="qure-loading"></div>
            <p className="mt-3">Loading library...</p>
          </div>
        </div>
      </QureLayout>
    );
  }

  return (
    <QureLayout>
      <div className="qure-library-container">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-header-content">
            <div className="page-title-section">
              <h1>Experiment Library</h1>
              <p className="page-subtitle">Your Mixed Reality experiment assignments and progress</p>
            </div>
            <div className="header-stats">
              <div className="stat-item">
                <span className="stat-number">{stats.total_experiments}</span>
                <span className="stat-label">Total</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{completionPercentage}%</span>
                <span className="stat-label">Complete</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="quick-stats">
          <div className="quick-stat upcoming" onClick={() => scrollToSection('upcoming')}>
            <div className="quick-stat-number">{stats.upcoming_count}</div>
            <div className="quick-stat-label">Upcoming</div>
          </div>
          <div className="quick-stat missed" onClick={() => scrollToSection('missed')}>
            <div className="quick-stat-number">{stats.missed_count}</div>
            <div className="quick-stat-label">Missed</div>
          </div>
          <div className="quick-stat completed" onClick={() => scrollToSection('completed')}>
            <div className="quick-stat-number">{stats.completed_count}</div>
            <div className="quick-stat-label">Completed</div>
          </div>
        </div>

        {/* Upcoming Experiments Section */}
        <div className="experiment-section" id="upcoming">
          <div className="section-header">
            <h2 className="section-title">
              <i className="bi bi-clock text-primary"></i>
              Upcoming Experiments
            </h2>
            <span className="section-count">{stats.upcoming_count}</span>
          </div>
          <div className="experiments-grid">
            {experiments.upcoming.length > 0 ? (
              experiments.upcoming.map((exp, index) => {
                const daysUntil = exp.days_until || 0;
                let status = 'upcoming';
                let statusText = 'Upcoming';
                if (daysUntil === 0) {
                  status = 'today';
                  statusText = 'Due Today';
                } else if (daysUntil === 1) {
                  statusText = 'Tomorrow';
                } else {
                  statusText = `In ${daysUntil} days`;
                }

                return (
                  <div 
                    key={exp.schedule_id || index} 
                    className="experiment-card" 
                    onClick={() => viewExperiment('upcoming', index)}
                  >
                    <div className="mr-badge">MR</div>
                    <div className="card-header">
                      <div className="project-name">
                        <i className="bi bi-folder"></i>
                        {exp.project_name || 'Unknown Project'}
                      </div>
                      <div className="experiment-title">
                        {exp.experiment_title || 'Untitled Experiment'}
                      </div>
                      <div className="experiment-meta">
                        <span className={`status-badge ${status}`}>
                          {statusText}
                        </span>
                        <span className="assignment-badge">{exp.assignment_type || 'Individual'}</span>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="experiment-details">
                        <div className="detail-row">
                          <i className="bi bi-calendar detail-icon"></i>
                          <span>{formatDate(exp.scheduled_date)}</span>
                        </div>
                        {exp.scheduled_time && (
                          <div className="detail-row">
                            <i className="bi bi-clock detail-icon"></i>
                            <span>{formatTime(exp.scheduled_time)}</span>
                          </div>
                        )}
                        <div className="detail-row">
                          <i className="bi bi-person detail-icon"></i>
                          <span>Assigned by {exp.assigned_by || 'Unknown'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="card-footer">
                      <span className="text-muted">Click to view details</span>
                      <button className="view-btn">
                        <i className="bi bi-eye"></i>
                        View
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-state">
                <i className="bi bi-calendar-check"></i>
                <h4>No Upcoming Experiments</h4>
                <p>You're all caught up! No experiments scheduled for the near future.</p>
              </div>
            )}
          </div>
        </div>

        {/* Missed Experiments Section */}
        {experiments.missed.length > 0 && (
          <div className="experiment-section" id="missed">
            <div className="section-header">
              <h2 className="section-title">
                <i className="bi bi-exclamation-triangle text-danger"></i>
                Missed Experiments
              </h2>
              <span className="section-count">{stats.missed_count}</span>
            </div>
            <div className="experiments-grid">
              {experiments.missed.map((exp, index) => (
                <div 
                  key={exp.schedule_id || index} 
                  className="experiment-card" 
                  onClick={() => viewExperiment('missed', index)}
                >
                  <div className="mr-badge">MR</div>
                  <div className="card-header">
                    <div className="project-name">
                      <i className="bi bi-folder"></i>
                      {exp.project_name || 'Unknown Project'}
                    </div>
                    <div className="experiment-title">
                      {exp.experiment_title || 'Untitled Experiment'}
                    </div>
                    <div className="experiment-meta">
                      <span className="status-badge missed">
                        {exp.days_overdue || 0} days overdue
                      </span>
                      <span className="assignment-badge">{exp.assignment_type || 'Individual'}</span>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="experiment-details">
                      <div className="detail-row">
                        <i className="bi bi-calendar detail-icon"></i>
                        <span>Was due {formatDate(exp.scheduled_date)}</span>
                      </div>
                      <div className="detail-row">
                        <i className="bi bi-person detail-icon"></i>
                        <span>Assigned by {exp.assigned_by || 'Unknown'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="card-footer">
                    <span className="text-danger">Action required</span>
                    <button className="view-btn">
                      <i className="bi bi-eye"></i>
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed Experiments Section */}
        {experiments.completed.length > 0 && (
          <div className="experiment-section" id="completed">
            <div className="section-header">
              <h2 className="section-title">
                <i className="bi bi-check-circle text-success"></i>
                Recent Completions
              </h2>
              <span className="section-count">{stats.completed_count}</span>
            </div>
            <div className="experiments-grid">
              {experiments.completed.map((exp, index) => (
                <div 
                  key={exp.schedule_id || index} 
                  className="experiment-card" 
                  onClick={() => viewExperiment('completed', index)}
                >
                  <div className="mr-badge">MR</div>
                  <div className="card-header">
                    <div className="project-name">
                      <i className="bi bi-folder"></i>
                      {exp.project_name || 'Unknown Project'}
                    </div>
                    <div className="experiment-title">
                      {exp.experiment_title || 'Untitled Experiment'}
                    </div>
                    <div className="experiment-meta">
                      <span className="status-badge completed">
                        <i className="bi bi-check-circle me-1"></i>
                        Completed
                      </span>
                      <span className="assignment-badge">{exp.assignment_type || 'Individual'}</span>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="experiment-details">
                      <div className="detail-row">
                        <i className="bi bi-calendar-check detail-icon"></i>
                        <span>Completed {formatDate(exp.scheduled_date)}</span>
                      </div>
                      <div className="detail-row">
                        <i className="bi bi-person detail-icon"></i>
                        <span>Assigned by {exp.assigned_by || 'Unknown'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="card-footer">
                    <span className="text-success">Successfully completed</span>
                    <button className="view-btn">
                      <i className="bi bi-eye"></i>
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Experiment Details Modal */}
      {showModal && selectedExperiment && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="modal-content-wrapper" ref={modalRef} onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-flask me-2"></i>
                  Experiment Details
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="detail-section">
                  <h6><i className="bi bi-folder"></i> Project</h6>
                  <p>{selectedExperiment.project_name || 'Unknown Project'}</p>
                </div>
                
                <div className="detail-section">
                  <h6><i className="bi bi-flask"></i> Experiment Title</h6>
                  <p>{selectedExperiment.experiment_title || 'Untitled Experiment'}</p>
                </div>
                
                <div className="detail-section">
                  <h6><i className="bi bi-info-circle"></i> Status</h6>
                  {selectedExperiment.category === 'completed' ? (
                    <span className="badge" style={{background: '#4CAF50', color: 'white', fontSize: '0.9rem', padding: '0.5rem 1rem'}}>
                      <i className="bi bi-check-circle me-1"></i>Completed Successfully
                    </span>
                  ) : selectedExperiment.category === 'missed' ? (
                    <span className="badge" style={{background: '#F44336', color: 'white', fontSize: '0.9rem', padding: '0.5rem 1rem'}}>
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      {selectedExperiment.days_overdue || 0} Days Overdue
                    </span>
                  ) : (
                    <span className="badge" style={{background: '#2196F3', color: 'white', fontSize: '0.9rem', padding: '0.5rem 1rem'}}>
                      <i className="bi bi-clock me-1"></i>
                      {selectedExperiment.days_until === 0 ? 'Due Today' : 
                       selectedExperiment.days_until === 1 ? 'Due Tomorrow' : 
                       `Due in ${selectedExperiment.days_until} days`}
                    </span>
                  )}
                </div>
                
                <div className="detail-section">
                  <h6><i className="bi bi-calendar-event"></i> Scheduled Date & Time</h6>
                  <p>
                    <i className="bi bi-calendar me-1"></i>
                    {formatFullDate(selectedExperiment.scheduled_date)}
                    {selectedExperiment.scheduled_time && (
                      <>
                        <br />
                        <i className="bi bi-clock me-1"></i>
                        {formatTime(selectedExperiment.scheduled_time)}
                      </>
                    )}
                  </p>
                </div>
                
                <div className="detail-section">
                  <h6><i className="bi bi-person"></i> Assignment Details</h6>
                  <p>
                    <strong>Assigned by:</strong> {selectedExperiment.assigned_by || 'Unknown'}<br />
                    <strong>Assignment type:</strong> {selectedExperiment.assignment_type || 'Individual'}
                  </p>
                </div>
                
                {selectedExperiment.prt_description && (
                  <div className="detail-section">
                    <h6><i className="bi bi-file-text"></i> Description</h6>
                    <p>{selectedExperiment.prt_description}</p>
                  </div>
                )}
                
                <div className="detail-section">
                  <h6><i className="bi bi-list-ol"></i> Experiment Steps</h6>
                  <div className="steps-list">
                    {selectedExperiment.parsedSteps && selectedExperiment.parsedSteps.length > 0 ? (
                      <ol>
                        {selectedExperiment.parsedSteps.map((step, idx) => (
                          <li key={idx}>{step.replace(/Step \d+:\s*/, '').trim()}</li>
                        ))}
                      </ol>
                    ) : (
                      <p className="text-muted">No detailed steps available.</p>
                    )}
                  </div>
                </div>
                
                <div className="detail-section">
                  <h6><i className="bi bi-badge-vr"></i> Technology</h6>
                  <span className="badge" style={{background: 'linear-gradient(135deg, #2196F3, #D95A17)', color: 'white', fontSize: '0.9rem', padding: '0.5rem 1rem'}}>
                    Mixed Reality (MR)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </QureLayout>
  );
};

export default QureLibrary;
